/**
 * ChatDashboardTable - Tabela expansível de departamentos e usuários
 */

import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { PlainHeadCell } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
export interface DepartmentStats {
  id: string;
  name: string;
  newMessages: number;
  inProgress: number;
  waiting: number;
  returned: number;
  users: UserStats[];
}

export interface UserStats {
  id: string;
  name: string;
  picture?: string;
  newMessages: number;
  inProgress: number;
  waiting: number;
  returned: number;
}

interface ChatDashboardTableProps {
  departments: DepartmentStats[];
  individualUsers: UserStats[];
}

export const ChatDashboardTable: React.FC<ChatDashboardTableProps> = ({ departments, individualUsers }) => {
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());

  const toggleDepartment = (deptId: string) => {
    setExpandedDepartments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(deptId)) {
        newSet.delete(deptId);
      } else {
        newSet.add(deptId);
      }
      return newSet;
    });
  };

  return (
    <div
      className="overflow-x-auto rounded-lg border"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <table className="w-full">
        <thead>
          <tr
            className="border-b"
            style={{
              backgroundColor: 'var(--theme-table-header-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <PlainHeadCell label="Nome" />
            <PlainHeadCell label="Novas Mensagens" align="center" />
            <PlainHeadCell label="Em Atendimento" align="center" />
            <PlainHeadCell label="Em Espera" align="center" />
            <PlainHeadCell label="Retorno" align="center" />
            <PlainHeadCell label="Ações" align="center" />
          </tr>
        </thead>
        <tbody>
          {departments.map((dept) => {
            const isExpanded = expandedDepartments.has(dept.id);

            return (
              <React.Fragment key={dept.id}>
                {/* Department Row */}
                <tr
                  className="border-b hover:bg-opacity-50 transition-colors"
                  style={{
                    borderColor: 'var(--theme-card-border)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-semibold"
                        style={{
                          backgroundColor: 'var(--theme-primary-background)',
                          color: 'var(--theme-primary)',
                        }}
                      >
                        {dept.name.charAt(0)}
                      </div>
                      <span className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                        {dept.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                      style={{
                        backgroundColor: dept.newMessages > 0 ? 'var(--theme-error-background)' : 'var(--theme-card-border)',
                        color: dept.newMessages > 0 ? 'var(--theme-error-color)' : 'var(--theme-muted-text)',
                      }}
                    >
                      {dept.newMessages}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                      style={{
                        backgroundColor: dept.inProgress > 0 ? 'var(--theme-primary-background)' : 'var(--theme-card-border)',
                        color: dept.inProgress > 0 ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
                      }}
                    >
                      {dept.inProgress}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                      style={{
                        backgroundColor: dept.waiting > 0 ? 'var(--theme-warning-background)' : 'var(--theme-card-border)',
                        color: dept.waiting > 0 ? 'var(--theme-warning-color)' : 'var(--theme-muted-text)',
                      }}
                    >
                      {dept.waiting}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                      style={{
                        backgroundColor: dept.returned > 0 ? 'var(--theme-info-background)' : 'var(--theme-card-border)',
                        color: dept.returned > 0 ? 'var(--theme-info-color)' : 'var(--theme-muted-text)',
                      }}
                    >
                      {dept.returned}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleDepartment(dept.id)}
                      className="p-2 rounded-lg transition-colors"
                      data-testid={`chat-dashboard-dept-toggle-${dept.id}`}
                      aria-expanded={isExpanded}
                      style={{ color: 'var(--theme-muted-text)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                        e.currentTarget.style.color = 'var(--theme-card-text)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--theme-muted-text)';
                      }}
                    >
                      {isExpanded ? (
                        <Icon name="chevron-down" className="w-5 h-5" />
                      ) : (
                        <Icon name="chevron-right" className="w-5 h-5" />
                      )}
                    </button>
                  </td>
                </tr>

                {/* User Rows (expanded) */}
                {isExpanded &&
                  dept.users.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b hover:bg-opacity-50 transition-colors"
                      style={{
                        borderColor: 'var(--theme-card-border)',
                        backgroundColor: 'var(--theme-button-ghost-hover)',
                      }}
                    >
                      <td className="px-4 py-3 pl-16">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8" isSquircle>
                            <AvatarImage src={user.picture} alt={user.name} isSquircle />
                            <AvatarFallback isSquircle>
                              <span className="text-indigo-600 font-semibold text-xs">
                                {user.name.charAt(0)}
                              </span>
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                            {user.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: user.newMessages > 0 ? 'var(--theme-error-background)' : 'var(--theme-card-border)',
                            color: user.newMessages > 0 ? 'var(--theme-error-color)' : 'var(--theme-muted-text)',
                          }}
                        >
                          {user.newMessages}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: user.inProgress > 0 ? 'var(--theme-primary-background)' : 'var(--theme-card-border)',
                            color: user.inProgress > 0 ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
                          }}
                        >
                          {user.inProgress}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: user.waiting > 0 ? 'var(--theme-warning-background)' : 'var(--theme-card-border)',
                            color: user.waiting > 0 ? 'var(--theme-warning-color)' : 'var(--theme-muted-text)',
                          }}
                        >
                          {user.waiting}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: user.returned > 0 ? 'var(--theme-info-background)' : 'var(--theme-card-border)',
                            color: user.returned > 0 ? 'var(--theme-info-color)' : 'var(--theme-muted-text)',
                          }}
                        >
                          {user.returned}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {/* Empty cell for actions column */}
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}

          {/* Divider entre equipes e usuários individuais */}
          {departments.length > 0 && individualUsers.length > 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-0">
                <div
                  className="border-t-2"
                  style={{ borderColor: 'var(--theme-card-border)' }}
                />
              </td>
            </tr>
          )}

          {/* Usuários Individuais (sem equipe) */}
          {individualUsers.map((user) => (
            <tr
              key={user.id}
              className="border-b hover:bg-opacity-50 transition-colors"
              style={{
                borderColor: 'var(--theme-card-border)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10" isSquircle>
                    <AvatarImage src={user.picture} alt={user.name} isSquircle />
                    <AvatarFallback isSquircle>
                      <span className="text-indigo-600 font-semibold">
                        {user.name.charAt(0)}
                      </span>
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                    {user.name}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-center">
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: user.newMessages > 0 ? 'var(--theme-error-background)' : 'var(--theme-card-border)',
                    color: user.newMessages > 0 ? 'var(--theme-error-color)' : 'var(--theme-muted-text)',
                  }}
                >
                  {user.newMessages}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: user.inProgress > 0 ? 'var(--theme-primary-background)' : 'var(--theme-card-border)',
                    color: user.inProgress > 0 ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
                  }}
                >
                  {user.inProgress}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: user.waiting > 0 ? 'var(--theme-warning-background)' : 'var(--theme-card-border)',
                    color: user.waiting > 0 ? 'var(--theme-warning-color)' : 'var(--theme-muted-text)',
                  }}
                >
                  {user.waiting}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: user.returned > 0 ? 'var(--theme-info-background)' : 'var(--theme-card-border)',
                    color: user.returned > 0 ? 'var(--theme-info-color)' : 'var(--theme-muted-text)',
                  }}
                >
                  {user.returned}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                {/* Sem botão de ações para usuários individuais */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
